import React from "react";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import { hasCheckSecurity } from "@/util/common_function";
import { useFieldSecurity } from "@/contexts/FieldSecurityContext";
import {
  useFieldVerification,
  makeVerifyKeyDownCapture,
} from "@/contexts/FieldVerificationContext";

/**
 * SecureFormItem
 * Wraps any form field with automatic field-level security from context.
 *
 * @param {string}   fieldName         - The field name used in hasCheckSecurity (e.g. "lName")
 * @param {object}   formItemProps     - All props forwarded to <FormItem> (name, label, rules, etc.)
 * @param {boolean}  [overrideDisabled]- Extra disable condition on top of security (e.g. loading state)
 * @param {string}   [verifyKey]       - Key used to look up jacket-verification state; defaults to fieldName
 * @param {boolean}  [skipVocab]       - Keep the label as written, ignoring Vocabulary renames. For a
 *                                       label that shares its text with a renamed one but is a different
 *                                       field (PCI only renamed labels wrapped in Vocab()).
 * @param {ReactNode} children         - The input element (TextField, Select, Checkbox, etc.)
 *
 * Usage:
 *   <SecureFormItem fieldName="lName" formItemProps={{ name: "lName", label: "Last Name" }}>
 *     <TextField />
 *   </SecureFormItem>
 */
const SecureFormItem = ({
  fieldName,
  formItemProps = {},
  overrideDisabled = false,
  entityOverride,
  verifyKey,
  skipVocab = false,
  children,
}) => {
  const { entity, startingPoint, bookingsFieldsEnabled } = useFieldSecurity();
  const { requireVerification, isUnverified, markVerified } =
    useFieldVerification();

  const resolvedEntity = entityOverride ?? entity;  // ← use override if provided
  const resolvedVerifyKey = verifyKey ?? fieldName;
  const unverified = requireVerification && isUnverified(resolvedVerifyKey);

  const canModify = hasCheckSecurity(
    "Functionality",
    "field",
    "MODIFY",
    "",
    "",
    resolvedEntity,
    fieldName,
    startingPoint
  );

  const canView = hasCheckSecurity(
    "Functionality",
    "field",
    "VIEW",
    "",
    "",
    resolvedEntity,
    fieldName,
    startingPoint
  );

  // Preserve any className already on the child — only append
  // security/verification classes, never wipe out existing ones (e.g. "ant-text-dark").
  const existingClassName = children.props.className || "";
  const secureClassName = [
    existingClassName,
    canView ? "" : "input-control-mask visibility-hidden",
    unverified ? "field--pink address-select--pink" : "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  const existingOnChange = children.props.onChange;
  const existingOnKeyDown = children.props.onKeyDown;
  const secureChildProps = {
    disabled: !canModify || !bookingsFieldsEnabled || overrideDisabled,
    className: secureClassName,
    onChange: unverified
      ? (...args) => {
          markVerified(resolvedVerifyKey);
          existingOnChange?.(...args);
        }
      : existingOnChange,
    // Ctrl+Q clears verification without requiring an edit. Attached directly on
    // the field (not a wrapping element) — Form.Item clones only its direct child
    // to wire up value/onChange, so a wrapper here would silently break form binding.
    onKeyDown: unverified
      ? (e) => {
          makeVerifyKeyDownCapture(resolvedVerifyKey, unverified, markVerified)?.(e);
          existingOnKeyDown?.(e);
        }
      : existingOnKeyDown,
  };

  return (
    // FormItem applies the site's label renames; fieldName is the security
    // field name, which matches Vocabulary rows keyed on a DB field name.
    // formItemProps.vocabFieldName overrides it when the two differ.
    <FormItem
      {...formItemProps}
      vocabFieldName={formItemProps.vocabFieldName ?? fieldName}
      skipVocab={skipVocab}
    >
      {React.cloneElement(children, secureChildProps)}
    </FormItem>
  );
};

export default SecureFormItem;